import React, { useEffect, useState } from "react";
import BasicSettings from "./BasicSettings";
import JoinSettings from "./JoinSettings";
import MiscSettings from "./MiscSettings";
import ChatSettings from "./ChatSettings";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import TabBar from "components/shared/TabBar";
import { filter, forEach, isEmpty, keys, omit, some } from "lodash";
import FormActions from "components/shared/form/fields/FormActions";
import { Property } from "components/groups/types";
import { useLoadItem } from "components/appCreator/api";
import { Group } from "../../../@types";
import usePublishAllVolatile from "helpers/files/usePublishAllVolatile";

type GroupFormArgs = {
  group: Group;
  app?: {
    properties: Property[];
  };
  isAppLoading: boolean;
  onSubmit: (
    values: Group & { profile: { [key: string]: string | boolean } },
    props: { externalAppId?: string; externalItemId?: string },
  ) => void;
};

type FormData = {
  group_category_id: string;
  name: string;
  access: string;
  moderated_join: boolean;
  members_can_invite: boolean;
  chat: string;
  [key: string]: any;
};

export default function GroupForm({
  group,
  app,
  isAppLoading,
  onSubmit,
}: GroupFormArgs) {
  const [selectedTabIndex, selectTabIndex] = useState(0);
  const networkId = Preload.current_network.id;
  const {
    can,
    name,
    group_category_id,
    access,
    moderated_join,
    members_can_invite,
    chat,
    sensitive,
    active_main_group,
  } = group;
  const externalAppId = `network_${networkId}:group_category_${group_category_id}:group_profile`;
  const externalItemId = group.id;
  const miscSettings: React.ReactNode[] = [];
  const [filesLoading, setFilesLoading] = useState(false);

  const publishAllVolatile = usePublishAllVolatile();

  const { isLoading: isItemLoading, data: item } = useLoadItem({
    externalAppId,
    externalItemId,
  });

  const methods = useForm<FormData>({
    values: {
      group_category_id,
      name,
      access,
      moderated_join,
      members_can_invite,
      chat,
      ...item?.values,
      profile_image:
        item?.values?.profile_image && item?.values?.profile_image["image_id"]
          ? [item?.values.profile_image]
          : null,
    },
  });
  const { handleSubmit, control } = methods;

  const selectedProfileImage = useWatch({ control, name: "profile_image" });

  useEffect(() => {
    setFilesLoading(
      some(selectedProfileImage, {
        state: "uploading",
      }),
    );
  }, [selectedProfileImage]);

  if (chat) {
    miscSettings.push(<ChatSettings />);
  }

  function onValidationError(errors) {
    forEach(document.getElementsByClassName("field-error"), (element) => {
      element?.classList.remove("field-error");
    });

    forEach(errors, (error) => {
      error?.ref?.classList.add("field-error");
      toastr.error(error?.message || I18n.t("generic_error"));
    });
  }

  async function submit(data: FormData) {
    const profile = omit(data, [
      "group_category_id",
      "name",
      "access",
      "moderated_join",
      "members_can_invite",
      "chat",
    ]);
    const groupData = omit(data, keys(profile));

    if (!isEmpty(selectedProfileImage)) {
      const result = await publishAllVolatile({
        values: {
          profile_image: profile.profile_image,
        },
        storageDirectory: "images",
      });
      profile.profile_image = filter(
        result.profile_image,
        (image) => !image._destroy,
      );
    }

    onSubmit(
      { ...group, ...groupData, profile },
      { externalAppId, externalItemId },
    );
  }

  const tabs = [
    {
      label: I18n.t("js.groups.edit.basic_settings"),
      getTabContent: () => (
        <BasicSettings
          app={app}
          isLoading={isAppLoading || isEmpty(app) || isItemLoading}
        />
      ),
    },
    {
      label: I18n.t("js.groups.edit.join_settings"),
      getTabContent: () => (
        <JoinSettings
          canCreateHidden={can.create_hidden}
          isActiveMainGroup={active_main_group}
          sensitive={sensitive}
        />
      ),
    },
    {
      label: I18n.t("js.groups.edit.misc_settings"),
      getTabContent: () => <MiscSettings settings={miscSettings} />,
    },
  ];

  const { getTabContent } = tabs[selectedTabIndex];

  return (
    <FormProvider {...methods}>
      <form className="mb-6" onSubmit={handleSubmit(submit, onValidationError)}>
        <TabBar
          tabs={tabs}
          selectTabIndex={selectTabIndex}
          selectedTabIndex={selectedTabIndex}
        />
        <div className="tab-content group-settings mt-6">{getTabContent()}</div>
        <div className="form-horizontal mt-6">
          <FormActions saveDisabled={filesLoading} />
        </div>
      </form>
    </FormProvider>
  );
}
